import Grid from '@mui/material/Grid';
import React from 'react';
import { berechneGesamtzahlungsbetrag, berechneOffenenBetrag } from '../shared/util';
import { Vorgang, Zahlungseingang } from '../types';
import { Euro } from './common/Euro';
import Formular from './common/Formular';
import Tabelle from './Tabelle/Tabelle';
import { center } from '../assets/sharedStyles';
import { MEHRWERTSTEUER } from '../shared/domain/vorgang';
import { enqueueSnackbar } from 'notistack';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly zahlungsimportErlaubt: boolean;
  readonly rechnungsnummer: string;
  readonly setOpenUmbuchenDialog: (open: boolean) => void;
  readonly setZahlungseingangZumUmbuchen: (zahlungseingang: Zahlungseingang) => void;
  readonly setZahlungseingangZumLoeschen: (zahlungseingang: Zahlungseingang) => void;
};

export function Zahlungseingaenge({
  vorgang,
  isLoading,
  zahlungsimportErlaubt,
  rechnungsnummer,
  setOpenUmbuchenDialog,
  setZahlungseingangZumUmbuchen,
  setZahlungseingangZumLoeschen
}: Props) {
  const holeRechnungsbetrag = (rechnungsnummer: string | null) => {
    return vorgang.dateien?.find((datei) => datei.dateiname === rechnungsnummer + '.pdf')?.wert ?? 0;
  };

  const holeRechnungsbetragMitMwSt = (rechnungsnummer: string | null) => {
    return holeRechnungsbetrag(rechnungsnummer) * (1 + MEHRWERTSTEUER / 100);
  };

  return (
    <Grid size={{ xs: 12, lg: 12 }} key={rechnungsnummer}>
      <Formular
        ueberschrift={
          <>
            <span>Zahlungseingänge </span>
            <b>{rechnungsnummer}</b>
          </>
        }
      >
        <Tabelle
          actions={
            zahlungsimportErlaubt
              ? [
                  {
                    label: 'Umbuchen',
                    onClick: (zahlungseingang) => {
                      if (['Gutschrift', 'Storno'].includes(zahlungseingang.status)) {
                        enqueueSnackbar('Umbuchen ist für Gutschriften und Stornos nicht möglich.', { variant: 'error' });
                        return;
                      }
                      setOpenUmbuchenDialog(true);
                      setZahlungseingangZumUmbuchen(zahlungseingang);
                    },
                    disabled: isLoading || (vorgang.rechnungsnummern?.length ?? 0) === 0
                  },
                  {
                    label: 'Löschen',
                    onClick: setZahlungseingangZumLoeschen,
                    disabled: isLoading
                  }
                ]
              : null
          }
          columns={[
            { label: 'Rechnungsnummer', id: 'rechnungsnummer' },
            { label: 'Status', id: 'status' },
            { label: 'Datum', id: 'bezahldatum' },
            { label: 'Betrag', id: 'betrag', render: (zeile) => <Euro value={zeile.betrag}></Euro> },
            { label: 'Name', id: 'name' },
            { label: 'Beschreibung', id: 'beschreibung' }
          ]}
          tableData={vorgang.zahlungseingang?.filter((zahlung) => zahlung.rechnungsnummer === rechnungsnummer) ?? []}
          disabled={isLoading}
          isLoading={isLoading}
        />
        <Grid sx={center}>
          <b>
            Rechnungsbetrag brutto: <Euro value={holeRechnungsbetragMitMwSt(rechnungsnummer)}></Euro>
          </b>
          <b>
            Summe Zahlungseingänge:{' '}
            <Euro value={berechneGesamtzahlungsbetrag(vorgang.zahlungseingang?.filter((zahlung) => zahlung.rechnungsnummer === rechnungsnummer) ?? [])}></Euro>
          </b>
          <b>
            Offener Betrag:{' '}
            <Euro
              value={
                holeRechnungsbetragMitMwSt(rechnungsnummer) -
                berechneOffenenBetrag(vorgang.zahlungseingang?.filter((zahlung) => zahlung.rechnungsnummer === rechnungsnummer) ?? [])
              }
            ></Euro>
          </b>
        </Grid>
      </Formular>
    </Grid>
  );
}
